import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../styles.css";
import {API_BASE_URL, STUDY_PLAN_API} from "../config/api";

const GenerateExerciseModal = ({ isOpen, onClose, onGenerateSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    grade: "",
    category: "",
    sub_category: "",
    sub_sub_category: "",
    sub_sub_sub_category: "",
    type: "",
    fonttype: "medium",
  });
  const [categoriesData, setCategoriesData] = useState({
    categories: [],
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateExercise = async (e) => {
    e.preventDefault();
    console.log("Generate button clicked, formData:", formData);

    if (!formData.grade || !formData.category || !formData.type) {
      console.log("Validation failed: Missing required fields");
      Swal.fire({
        title: t("error"),
        text: t("generateExerciseModal.errors.requiredFields"),
        icon: "error",
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/exercises/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || t("generateExerciseModal.errors.generateFailed"));
      }

      if (!data.exercise) {
        throw new Error(t("generateExerciseModal.errors.invalidResponse"));
      }

      onGenerateSuccess(data.exercise);
      onClose();

      Swal.fire({
        title: t("success"),
        text: t("generateExerciseModal.success.exerciseGenerated"),
        icon: "success",
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Generate error:", error);
      Swal.fire({
        title: t("error"),
        text: error.message || t("generateExerciseModal.errors.generateFailed"),
        icon: "error",
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
    } finally {
      console.log("Resetting generating state");
      setGenerating(false);
    }
  };

  const fetchCategories = async (grade) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(STUDY_PLAN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ degree: grade }),
        mode: "cors",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${t("generateExerciseModal.errors.httpError")} ${response.status}: ${errorText || t("generateExerciseModal.errors.badRequest")}`);
      }

      const data = await response.json();
      setCategoriesData({ categories: Array.isArray(data) ? data : [] });
    } catch (err) {
      console.error("Fetch Error (Categories):", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.grade && isOpen) {
      fetchCategories(formData.grade);
    }
  }, [formData.grade, isOpen]);

  useEffect(() => {
    if (formData.grade) {
      setFormData(prev => ({
        ...prev,
        category: "",
        sub_category: "",
        sub_sub_category: "",
        sub_sub_sub_category: "",
      }));
    }
  }, [formData.grade]);

  const getSubCategories = (parentPath, level) => {
    let current = categoriesData.categories;
    const pathParts = parentPath.split(".").filter(Boolean);

    for (let i = 0; i < pathParts.length && current; i++) {
      if (Array.isArray(current)) {
        current = current.find(item => item.title === pathParts[i]);
      } else if (current?.sub_categories) {
        current = current.sub_categories.find(item => item.title === pathParts[i]);
      } else {
        return [];
      }
    }

    const subCategories = current?.sub_categories || [];
    return subCategories.map(item => ({
      title: item.title || t("Generate exercise"),
      hasSubCategories: !!item.sub_categories?.length,
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  if (loading && formData.grade) {
    return (
      <div className="generate-modal-overlay">
        <div className="generate-modal-loading">
          <p>{t("generateExerciseModal.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="generate-modal-overlay">
        <div className="generate-modal-error-container">
          <p className="generate-modal-error-text">{t("generateExerciseModal.error")}: {error}</p>
          <button
            onClick={() => fetchCategories(formData.grade)}
            className="generate-modal-button generate-modal-generate-button"
          >
            {t("generateExerciseModal.retry")}
          </button>
        </div>
      </div>
    );
  }

  const gradeOptions = ["1/2", "3/4", "5/6", "7/8", "9/10"];
  const categoryOptions = categoriesData.categories.map(cat => ({
    title: cat.title || t("Generate exercise"),
    hasSubCategories: !!cat.sub_categories?.length,
  }));
  const subCategoryOptions = formData.category ? getSubCategories(formData.category, 1) : [];
  const subSubCategoryOptions = formData.sub_category ? getSubCategories(`${formData.category}.${formData.sub_category}`, 2) : [];
  const subSubSubCategoryOptions = formData.sub_sub_category ? getSubCategories(`${formData.category}.${formData.sub_category}.${formData.sub_sub_category}`, 3) : [];

  return (
    <div className="generate-modal-overlay" onClick={onClose}>
      <div className="generate-modal-container" onClick={(e) => e.stopPropagation()}>
        <h3 className="generate-modal-title">{t("Generate exercise")}</h3>
        <form onSubmit={handleGenerateExercise}>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Grade")} *</label>
            <select
              name="grade"
              value={formData.grade}
              onChange={handleFormChange}
              className="generate-modal-select"
              required
            >
              <option value="" disabled>{t("Select Grade")}</option>
              {gradeOptions.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Category")} *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleFormChange}
              className="generate-modal-select"
              required
              disabled={!formData.grade || categoryOptions.length === 0}
            >
              <option value="" disabled>{t("Select Category")}</option>
              {categoryOptions.map(option => (
                <option key={option.title} value={option.title}>{option.title}</option>
              ))}
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Sub-Category")}</label>
            <select
              name="sub_category"
              value={formData.sub_category}
              onChange={handleFormChange}
              className="generate-modal-select"
              disabled={!formData.category || subCategoryOptions.length === 0}
            >
              <option value="">{t("Select Sub-Category")}</option>
              {subCategoryOptions.map(option => (
                <option key={option.title} value={option.title}>{option.title}</option>
              ))}
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Sub-Sub-Category")}</label>
            <select
              name="sub_sub_category"
              value={formData.sub_sub_category}
              onChange={handleFormChange}
              className="generate-modal-select"
              disabled={!formData.sub_category || subSubCategoryOptions.length === 0}
            >
              <option value="">{t("Select Sub-Sub-Category")}</option>
              {subSubCategoryOptions.map(option => (
                <option key={option.title} value={option.title}>{option.title}</option>
              ))}
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Sub-Sub-Sub-Category")}</label>
            <select
              name="sub_sub_sub_category"
              value={formData.sub_sub_sub_category}
              onChange={handleFormChange}
              className="generate-modal-select"
              disabled={!formData.sub_sub_category || subSubSubCategoryOptions.length === 0}
            >
              <option value="">{t("Select Sub-Sub-SubCategory")}</option>
              {subSubSubCategoryOptions.map(option => (
                <option key={option.title} value={option.title}>{option.title}</option>
              ))}
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("Exercise Type")} *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleFormChange}
              className="generate-modal-select"
              required
            >
              <option value="" disabled>{t("select Type")}</option>
              <option value="dropbox">{t("Dropbox")}</option>
              <option value="click">{t("Click")}</option>
              <option value="click_3_choix">{t("Click_3_choix")}</option>
              <option value="click_4_choix">{t("Click_4_choix")}</option>
              <option value="click_phrase">{t("Click_phrase")}</option>
              <option value="click_3_phrase">{t("Click_3_phrase")}</option>
              <option value="click_4_phrase">{t("Click_4_phrase")}</option>
              <option value="drag_and_drop_phrase">{t("Drag_and_drop_phrase")}</option>
              <option value="drag_and_drop_paragraphe">{t("Drag_and_drop_paragraphe")}</option>
              <option value="clavier_maths">{t("Clavier_maths")}</option>
              <option value="clavier_langue">{t("Clavier_langue")}</option>
              <option value="clavier2inputs">{t("Clavier2inputs")}</option>
              <option value="quizz">{t("Quizz")}</option>
            </select>
          </div>
          <div className="generate-modal-field">
            <label className="generate-modal-label">{t("fontType")}</label>
            <select
              name="fonttype"
              value={formData.fonttype}
              onChange={handleFormChange}
              className="generate-modal-select"
            >
              <option value="medium">{t("Medium")}</option>
              <option value="small">{t("Small")}</option>
              <option value="large">{t("large")}</option>
            </select>
          </div>
          <div className="generate-modal-button-container">
            <button
              type="button"
              onClick={onClose}
              className="generate-modal-button generate-modal-cancel-button"
              disabled={generating}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              onClick={() => console.log("Generate button clicked directly")}
              className="generate-modal-button generate-modal-generate-button"
              disabled={generating}
            >
              {generating ? t("generating") : t("Generate")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateExerciseModal;